<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import ContactsBulkActions from 'dashboard/components-next/Contacts/ContactsBulkActions/Index.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';

const props = defineProps({ contacts: { type: Array, required: true } });

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);

// Bulk operations state
const selectedContacts = ref([]);
const allContactsSelected = ref(false);
const showBulkActions = computed(() => true);

const updateContact = async updatedData => {
  try {
    await store.dispatch('contacts/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

// Bulk operations handlers
const handleContactSelect = ({ id, selected }) => {
  if (selected) {
    if (!selectedContacts.value.includes(id)) {
      selectedContacts.value.push(id);
    }
  } else {
    const index = selectedContacts.value.indexOf(id);
    if (index > -1) {
      selectedContacts.value.splice(index, 1);
    }
  }
  // Update allContactsSelected based on whether all contacts are selected
  allContactsSelected.value =
    selectedContacts.value.length === props.contacts.length &&
    props.contacts.length > 0;
};

const handleSelectAllContacts = selected => {
  if (selected) {
    selectedContacts.value = props.contacts.map(contact => contact.id);
    allContactsSelected.value = true;
  } else {
    selectedContacts.value = [];
    allContactsSelected.value = false;
  }
};

const handleDeleteContacts = async contactIds => {
  try {
    await Promise.all(
      contactIds.map(contactId => store.dispatch('contacts/delete', contactId))
    );
    selectedContacts.value = [];
    allContactsSelected.value = false;
    useAlert(t('CONTACTS_LAYOUT.BULK_ACTIONS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.BULK_ACTIONS.DELETE_ERROR'));
  }
};

const handleAssignLabels = async labels => {
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContacts.value,
      labels: { add: labels },
    });

    useAlert(t('CONTACTS_LAYOUT.BULK_ACTIONS.LABELS_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.BULK_ACTIONS.LABELS_ERROR'));
  }
};

const handleAddCustomAttributes = async attributes => {
  try {
    const customAttributes = attributes.reduce((acc, attr) => {
      acc[attr.key] = attr.value;
      return acc;
    }, {});

    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContacts.value,
      custom_attributes: customAttributes,
    });

    useAlert(t('CONTACTS_LAYOUT.BULK_ACTIONS.CUSTOM_ATTRIBUTES_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.BULK_ACTIONS.CUSTOM_ATTRIBUTES_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 px-6 pt-4 pb-6">
    <ContactsBulkActions
      v-if="showBulkActions"
      :contacts="contacts"
      :all-contacts-selected="allContactsSelected"
      :selected-contacts="selectedContacts"
      @select-all-contacts="handleSelectAllContacts"
      @delete-contacts="handleDeleteContacts"
      @assign-labels="handleAssignLabels"
      @add-custom-attributes="handleAddCustomAttributes"
    />
    <ContactsCard
      v-for="contact in contacts"
      :id="contact.id"
      :key="contact.id"
      :name="contact.name"
      :email="contact.email"
      :thumbnail="contact.thumbnail"
      :phone-number="contact.phoneNumber"
      :additional-attributes="contact.additionalAttributes"
      :availability-status="contact.availabilityStatus"
      :is-expanded="expandedCardId === contact.id"
      :is-updating="isUpdating"
      :is-selected="selectedContacts.includes(contact.id)"
      :show-checkbox="showBulkActions"
      @toggle="toggleExpanded(contact.id)"
      @update-contact="updateContact"
      @show-contact="onClickViewDetails"
      @select="handleContactSelect"
    />
  </div>
</template>
